import {
  computed,
  effectScope,
  onScopeDispose,
  ref,
  watch,
  type ComputedRef,
  type EffectScope,
} from 'vue'

import {
  GET_STARTED_PREFERENCE_KEY,
  isGetStartedAvailable,
  normalizeGetStartedPreference,
} from '../service/getStartedAccess'

import { useAccount, useRbac, useWorkspace, useWorkspacePreferences } from '@/features/account'

/** Legacy `DefaultLayout.vue` re-evaluated the window once a minute so the nav entry expires on time. */
const CLOCK_MS = 60_000

export interface GetStartedAccess {
  /** The nav entry, the route guard and the landing redirect all read this one answer. */
  available: ComputedRef<boolean>
  /** Resolves once the selected workspace's `get_started_progress` preference has been read. */
  ensureLoaded: () => Promise<void>
}

let shared: { scope: EffectScope; access: GetStartedAccess } | null = null

function createGetStartedAccess(): GetStartedAccess {
  const account = useAccount()
  const workspace = useWorkspace()
  const rbac = useRbac()
  const preferences = useWorkspacePreferences()
  const preference = preferences.preference<unknown>(GET_STARTED_PREFERENCE_KEY)
  const now = ref(Date.now())
  const clock = setInterval(() => {
    now.value = Date.now()
  }, CLOCK_MS)
  onScopeDispose(() => clearInterval(clock))

  async function ensureLoaded(): Promise<void> {
    if (account.accountId.value === undefined || workspace.workspaceId.value === undefined) return
    try {
      await preferences.get(GET_STARTED_PREFERENCE_KEY)
    } catch {
      // An unreadable preference reads as "not hidden" — the legacy default.
    }
  }

  watch(
    () => [account.accountId.value, workspace.workspaceId.value],
    () => void ensureLoaded(),
    { immediate: true },
  )

  return {
    available: computed(() =>
      isGetStartedAvailable(workspace.workspaces.value, {
        onlyDashboard: rbac.onlyDashboard.value,
        hidden: normalizeGetStartedPreference(preference.value.value).hidden,
        now: now.value,
      }),
    ),
    ensureLoaded,
  }
}

/**
 * One app-lifetime instance in a detached scope: the sidebar provider, the route guard and the
 * boot landing are not component setups, and each would otherwise start its own clock and its own
 * preference watcher.
 */
export function useGetStartedAccess(): GetStartedAccess {
  if (!shared) {
    const scope = effectScope(true)
    const access = scope.run(createGetStartedAccess) as GetStartedAccess
    shared = { scope, access }
  }
  return shared.access
}

/** Test seam — stops the shared clock/watchers so each spec starts from a fresh instance. */
export function resetGetStartedAccess(): void {
  shared?.scope.stop()
  shared = null
}
